var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { AwsUi } from '../../../core/runtime/src';
import { removeClass, addClass } from '../../../core/utils/src/dom';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { Icon } from '../../icon/src';
var getDerivedId = function (controlId, suffix) { return (controlId ? controlId + "-" + suffix : undefined); };
export var FormField = AwsUi.__addComponent('awsui-form-field', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            var _this = this;
            this.node.addEventListener('awsui:__controlidchange', this.__onControlIdChange.bind(this));
            this.__containedRadioGroupItems = [];
            this.__listenToPropertyChanges('__managedByRadioGroup', function (newValue, target) {
                if (newValue) {
                    _this.__setRadioGroupItems(target);
                }
                else {
                    _this.__unsetRadioGroupItem(target);
                }
            });
            this.__listenToPropertyChanges('ariaRequired', function (newValue, target) {
                var supportedComponents = ['awsui-radio-group', 'awsui-tiles'];
                if (supportedComponents.indexOf(target.component.__componentName) !== -1) {
                    _this.__discoveredAriaRequired = !!newValue;
                }
            });
        }
    },
    functions: {
        __isValid: function () {
            return !this.errorText;
        },
        __updateAriaSuggestions: function () {
            var target = this.__discoveredAriaTarget;
            var newValue = this.__discoveredControlId;
            if (target == null) {
                return;
            }
            // Components that can receive suggested ariaDescribedBy values
            var supportDescribedBy = [
                'awsui-input',
                'awsui-autosuggest',
                'awsui-textarea',
                'awsui-select-trigger',
                'awsui-time-input',
                'awsui-date-picker'
            ];
            if (supportDescribedBy.indexOf(target.component.__componentName) !== -1) {
                if (newValue) {
                    var describedby = [];
                    if (this.errorText) {
                        describedby.push(getDerivedId(newValue, 'error'));
                    }
                    if (this.description) {
                        describedby.push(getDerivedId(newValue, 'description'));
                    }
                    if (this.hintText) {
                        describedby.push(getDerivedId(newValue, 'hints'));
                    }
                    target.component.__suggestedAriaDescribedby = describedby.join(' ');
                }
                else {
                    // The controlId is false-y, probably because it was removed. We should also retract our suggestion
                    target.component.__suggestedAriaDescribedby = null;
                }
            }
            var supportLabelledBy = ['awsui-multiselect', 'awsui-select'];
            if (supportLabelledBy.indexOf(target.component.__componentName) !== -1) {
                if (newValue) {
                    target.component.__suggestedAriaLabelledby = getDerivedId(newValue, 'label');
                }
            }
        },
        __onControlIdChange: function (event) {
            event.stopPropagation();
            // Save the target and the control id
            this.__discoveredControlId = event.target.component.controlId;
            this.__discoveredAriaTarget = event.target;
            this.__updateAriaSuggestions();
        },
        __resetAriaRequired: function () {
            this.__discoveredAriaRequired = false;
        },
        __resetRadioGroupItems: function () {
            this.__containedRadioGroupItems.forEach(this.__unsetRadioGroupItem.bind(this));
        },
        __setRadioGroupItems: function (target) {
            if (this.__containedRadioGroupItems.indexOf(target) === -1) {
                this.__containedRadioGroupItems = __spreadArrays(this.__containedRadioGroupItems, [target]);
            }
            this.__radioGroupLabelId = target.component.name + "-label";
            target.component.__groupLabelId = this.__radioGroupLabelId;
        },
        __unsetRadioGroupItem: function (target) {
            this.__containedRadioGroupItems = this.__containedRadioGroupItems.filter(function (i) { return i != target; });
            target.component.__groupLabelId = null;
        }
    },
    render: function (m, h) {
        var _this = this;
        var shouldStretch = function (formField) {
            var hasControlColumnLayout = formField.querySelector('.awsui-form-field-control awsui-column-layout') !== null;
            removeClass(formField, 'awsui-form-field-stretch');
            removeClass(formField, 'awsui-form-field-no-stretch');
            if ((hasControlColumnLayout || _this.stretch) && _this.stretch !== false) {
                addClass(formField, 'awsui-form-field-stretch');
            }
            if (_this.stretch == false) {
                addClass(formField, 'awsui-form-field-no-stretch');
            }
        };
        var cb = new LegacyClassBuilder(this)
            .addClass('awsui-form-field')
            .addComponentClass('invalid', !this.__isValid());
        var controlId = this.controlId || this.__discoveredControlId;
        var fieldError = function () {
            if (!_this.errorText) {
                return;
            }
            return (AwsUi.__createElement("div", { className: "awsui-form-field-error" },
                AwsUi.__createElement(Icon, { name: "status-warning" }),
                h.region('errorText', 'span', { id: getDerivedId(controlId, 'error') })));
        };
        var label;
        if (this.__containedRadioGroupItems.length) {
            // for a radio group, labelling works in the opposite direction than normal
            // - inputs are `aria-labelledby` the label
            label = h.region('label', 'label', {
                className: 'awsui-form-field-label',
                id: this.__radioGroupLabelId
            });
        }
        else {
            label = h.region('label', 'label', {
                className: 'awsui-form-field-label',
                for: controlId,
                id: getDerivedId(controlId, 'label')
            });
        }
        var controls = function () {
            var controlCb = new LegacyClassBuilder(_this)
                .addComponentClass('control')
                .addClass('col-xxxs-12 col-xs-9')
                .addClass('awsui-invalid', !_this.__isValid());
            var secondaryControlCb = new LegacyClassBuilder(_this)
                .addComponentClass('secondary-control')
                .addClass('awsui-invalid', !_this.__isValid());
            return (AwsUi.__createElement("div", { className: "awsui-row" },
                h.region('control', 'div', { className: controlCb.toClassName() }),
                h.region('secondaryControl', 'div', { className: secondaryControlCb.toClassName() })));
        };
        var additionalAttributes = {};
        if (this.__containedRadioGroupItems.length) {
            additionalAttributes.role = 'radiogroup';
            additionalAttributes['aria-labelledby'] = this.__radioGroupLabelId;
            additionalAttributes['aria-required'] = this.__discoveredAriaRequired && 'true';
        }
        return (AwsUi.__createElement("div", __assign({ className: cb.toClassName(), lifecycleKey: "awsui-form-field", componentDidMount: shouldStretch, componentDidUpdate: shouldStretch }, additionalAttributes),
            label,
            h.region('description', 'div', {
                id: getDerivedId(controlId, 'description'),
                className: 'awsui-form-field-description'
            }),
            AwsUi.__createElement("div", { className: "awsui-grid awsui-form-field-controls" }, controls()),
            this.errorText || this.hintText ? (AwsUi.__createElement("div", { className: "awsui-form-field-hints" },
                fieldError(),
                h.region('hintText', 'div', {
                    id: getDerivedId(controlId, 'hints'),
                    className: 'awsui-form-field-hint'
                }))) : null));
    },
    properties: {
        // The id of the primary form control. Used for setting the label's "for" attribute
        // for accessibility.
        //
        // If unset, then the control group will automatically set the label to the id of
        // an inner form input component, like awsui-input. This only works well if
        // there is a single input component being used.
        controlId: {
            type: 'string'
        },
        // Overrides the default layout of the primary and secondary control.
        //
        // Possible effects of using this flag:
        // - In a single column layout, or without any column layout, if stretch is set to `true`,
        //   both controls will expand to 12 columns and stack, as it was a small screen
        // - In a multi column layout, if stretch is set to `false`, it will override the default behavior,
        //   and instead of the 12-12 columns it will get back to 9-3.
        stretch: {
            type: 'boolean',
            nullable: 'true'
        },
        __discoveredControlId: {
            type: 'string'
        },
        __discoveredAriaTarget: {
            type: 'object'
        },
        __discoveredAriaRequired: {
            type: 'boolean'
        },
        // The 'name' of a contained radio group, which is used
        // to generate an appropriate label ID
        __radioGroupLabelId: {
            type: 'string'
        },
        // A list of any contained radio group items - if this
        // is not empty, then the FormField will have aria role="radiogroup"
        __containedRadioGroupItems: {
            type: 'array'
        }
    },
    regions: {
        // Label of the form field.
        label: {},
        // Control region can be used to display an input element, a textarea etc.
        control: {
            isDefault: true,
            onChange: function () {
                this.__resetRadioGroupItems();
                this.__resetAriaRequired();
            }
        },
        // Secondary control can be used for custom actions and content.
        secondaryControl: {},
        // Detailed information about this form field, displayed below the label.
        description: {
            onChange: function () {
                this.__updateAriaSuggestions();
            }
        },
        // Hint text to be displayed below the control. Use this to provide additional
        // information about valid formats etc.
        hintText: {
            onChange: function () {
                this.__updateAriaSuggestions();
            }
        },
        // Text to show the control validation message. It will render the form field
        // as invalid.
        errorText: {
            onChange: function () {
                this.__updateAriaSuggestions();
            }
        }
    }
});
//# sourceMappingURL=index.js.map